/*

THEME DEFINITIONS

Boom UI is theme-based, which means no values are defined ad-hoc but taken from
theme via context. This allows to treat colors, fonts, shadows, spacings, etc.
as assets that can be replaced at any time without losing visual hierarchy and meaning of the UI.

To achieve that some rules must be followed.

- Theme MUST adhere to “System UI Theme Specification” unless overruled by
  further statements.
- Breakpoints MUST be defined in pixels.
  Reason for that is that relative values will change breakpoints absolute
  values if font size changes, which may lead to issues.
- Colors MUST have semantic names (e.g. `success` is preferred over `green`)
- Colors MUST not have any presentational words in their names (e.g. `green`,
  `light`, etc.)
- Colors values MAY be duplicate if there are two semantically different cases
  that use the same color (e.g. `primary` and `success` might have same value)
- Dimension-like values (font sizes, spacings, shadows) MUST be defined as
  arrays of values in order from smallest to largest

Recommended resources:
- System UI Theme Specification: https://system-ui.com/theme/
- Styled System docs: https://styled-system.com/

*/

import * as EmotionTheming from 'emotion-theming'
import * as React from 'react'
import PropTypes from 'prop-types'

import * as Configuration from '../configuration'

import boomTheme from './boom-theme'
import casinoSkyTheme from './casino-sky-theme'
import paradisePlayTheme from './paradise-play-theme'
import wettenliveTheme from './wettenlive-theme'

export const themes = {
  playboom: boomTheme,
  playboom24: boomTheme,
  freedomcasino: boomTheme,
  wettenlive: wettenliveTheme,
  casinosky: casinoSkyTheme,
  casinoskyjp: casinoSkyTheme,
  paradiseplay: paradisePlayTheme,
}

export function AlternativeTheme(props) {
  return (
    <EmotionTheming.ThemeProvider
      theme={theme => ({
        ...theme,
        colors: {
          ...themes[props.brand].colors,
          ...themes[props.brand].colors.__variants.alternative,
        },
      })}
    >
      {props.children}
    </EmotionTheming.ThemeProvider>
  )
}

export function PrimaryTheme(props) {
  return (
    <EmotionTheming.ThemeProvider
      theme={theme => ({
        ...theme,
        colors: {
          ...themes[props.brand].colors,
          ...themes[props.brand].colors.__variants.primary,
        },
      })}
    >
      {props.children}
    </EmotionTheming.ThemeProvider>
  )
}

export const Primary = Configuration.withBrand(PrimaryTheme)

export const Alternative = Configuration.withBrand(AlternativeTheme)

function ThemeProvider(props) {
  return (
    <EmotionTheming.ThemeProvider theme={themes[props.brand]}>
      <Primary>{props.children}</Primary>
    </EmotionTheming.ThemeProvider>
  )
}

export const Provider = Configuration.withBrand(ThemeProvider)

AlternativeTheme.propTypes = {
  brand: PropTypes.string,
  children: PropTypes.node,
}

PrimaryTheme.propTypes = {
  brand: PropTypes.string,
  children: PropTypes.node,
}

ThemeProvider.propTypes = {
  brand: PropTypes.string,
  children: PropTypes.node,
}
